import { useState, useMemo } from"react";
import { Search, X, Plus, Columns3 } from"lucide-react";
import { PCG } from"@/data/pcg";
import { PCG_INDEX, NATURE_LABELS, SENS_LABELS } from"@/lib/pcgUtils";
import { getClass } from"@/data/classMeta";

function norm(s) {
 return (s ||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"");
}

function AccountPicker({ onPick, excludeNums }) {
 const [q, setQ] = useState("");

 const results = useMemo(() => {
 const nq = norm(q.trim());
 if (!nq) return [];
 return PCG.filter(
 (a) => !excludeNums.includes(a.num) && (a.num.includes(nq) || norm(a.lib).includes(nq))
 ).slice(0, 8);
 }, [q, excludeNums]);

 return (
 <div className="bg-white border-2 border-dashed border-keyto rounded-xl p-4 h-full flex flex-col">
 <div className="flex items-center gap-2 mb-3 text-slate-500">
 <Plus size={16} />
 <span className="text-sm font-semibold">Ajouter un compte</span>
 </div>
 <div className="relative">
 <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
 <input
 data-testid="comparator-search"
 value={q}
 onChange={(e) => setQ(e.target.value)}
 placeholder="N° ou libellé…"
 className="w-full pl-8 pr-2 py-2 rounded-md border border-keyto bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 </div>
 <div className="mt-3 space-y-1 overflow-y-auto max-h-80">
 {q && results.length === 0 && (
 <p className="text-xs text-slate-400 italic py-2">Aucun résultat.</p>
 )}
 {results.map((a) => (
 <button
 key={a.num}
 data-testid={`comparator-pick-${a.num}`}
 onClick={() => {
 onPick(a.num);
 setQ("");
 }}
 className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-xs"
 >
 <span className="font-mono font-bold text-[#15294F] shrink-0">{a.num}</span>
 <span className="text-slate-700 truncate">{a.lib}</span>
 </button>
 ))}
 </div>
 </div>
 );
}

function Cell({ children, color }) {
 return (
 <div className="p-4 border-t border-keyto" style={color ? { borderTopColor: color } : {}}>
 {children}
 </div>
 );
}

function AccountColumn({ account, onRemove, onSelect }) {
 const cls = getClass(account.classe);
 const nature = NATURE_LABELS[account.nature] || NATURE_LABELS.mixte;
 const sens = SENS_LABELS[account.sens] || SENS_LABELS.M;
 return (
 <div className="bg-white border border-keyto rounded-xl overflow-hidden" style={{ borderTop: `4px solid ${cls.color.primary}` }} data-testid={`comparator-col-${account.num}`}>
 {/* Header */}
 <header className="p-4 bg-slate-50 border-b border-keyto flex items-start gap-2">
 <div className="flex-1 min-w-0">
 <span
 className="inline-block px-2 py-0.5 text-[10px] font-bold rounded text-white mb-2"
 style={{ background: cls.color.primary }}
 >
 CLASSE {cls.id}
 </span>
 <p className="font-mono font-extrabold text-3xl tracking-tight" style={{ color: cls.color.text }}>
 {account.num}
 </p>
 <p className="font-heading font-bold text-[#15294F] mt-1 leading-tight">{account.lib}</p>
 </div>
 <button
 data-testid={`comparator-remove-${account.num}`}
 onClick={() => onRemove(account.num)}
 className="shrink-0 p-1.5 rounded hover:bg-slate-200 text-slate-400"
 aria-label="Retirer"
 >
 <X size={14} />
 </button>
 </header>

 {/* Cells */}
 <Cell>
 <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Nature</p>
 <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded border ${nature.color}`}>{nature.label}</span>
 </Cell>
 <Cell>
 <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Sens normal</p>
 <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded border ${sens.color}`}>{sens.label}</span>
 </Cell>
 <Cell>
 <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">À quoi sert ce compte</p>
 <p className="text-sm text-slate-700 leading-relaxed">{account.usage ||"—"}</p>
 </Cell>
 <Cell>
 <p className="text-[10px] font-bold uppercase text-emerald-700 mb-1">Quand est-il débité ?</p>
 <p className="text-sm text-slate-700 leading-relaxed">{account.fonctionnement?.debit ||"—"}</p>
 </Cell>
 <Cell>
 <p className="text-[10px] font-bold uppercase text-rose-700 mb-1">Quand est-il crédité ?</p>
 <p className="text-sm text-slate-700 leading-relaxed">{account.fonctionnement?.credit ||"—"}</p>
 </Cell>
 {account.lies && account.lies.length > 0 && (
 <Cell>
 <p className="text-[10px] font-bold uppercase text-slate-400 mb-1.5">Comptes liés</p>
 <ul className="space-y-1">
 {account.lies.slice(0, 4).map((l, i) => (
 <li key={i} className="flex gap-2 text-xs">
 <button
 onClick={() => onSelect && onSelect(l.n)}
 className="font-mono font-bold text-[#2D6BE0] hover:underline shrink-0"
 >
 {l.n}
 </button>
 <span className="text-slate-600 leading-snug">{l.note}</span>
 </li>
 ))}
 </ul>
 </Cell>
 )}
 {account.erreurs && account.erreurs.length > 0 && (
 <Cell color="#B85724">
 <p className="text-[10px] font-bold uppercase text-[#B85724] mb-1.5">Erreurs fréquentes</p>
 <ul className="space-y-1.5">
 {account.erreurs.slice(0, 3).map((e, i) => (
 <li key={i} className="text-xs text-slate-700 leading-snug">
 · {e}
 </li>
 ))}
 </ul>
 </Cell>
 )}
 </div>
 );
}

export default function Comparateur({ onSelect }) {
 // Démarre avec deux comptes voisins pour illustrer
 const [picked, setPicked] = useState(["411","416"]);

 const add = (num) => setPicked((p) => (p.length < 4 ? [...p, num] : p));
 const remove = (num) => setPicked((p) => p.filter((n) => n !== num));

 const accounts = picked.map((n) => PCG_INDEX[n]).filter(Boolean);

 return (
 <div data-testid="comparateur-view" className="space-y-6">
 {/* Hero */}
 <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex items-start gap-4">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D6BE0]/10 border border-[#2D6BE0]/30 flex items-center justify-center">
 <Columns3 className="text-[#2D6BE0]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0] mb-3">
 Outil de différenciation
 </span>
 <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#15294F] leading-tight">
 Comparateur de comptes
 </h1>
 <p className="mt-3 text-slate-600 max-w-2xl">
 Affichez jusqu&apos;à <strong>4 comptes côte à côte</strong> pour
 comparer en un coup d&apos;œil leur nature, leur sens, leur usage
 et leurs erreurs fréquentes. Idéal pour lever les confusions
 classiques (401 vs 404, 411 vs 416, 281 vs 291…).
 </p>
 <div className="mt-3 flex flex-wrap gap-2">
 <span className="text-[11px] text-slate-500">Exemples :</span>
 {[["401","404"], ["411","416"], ["281","291"]].map(
 (combo, i) => (
 <button
 key={i}
 data-testid={`comparator-preset-${i}`}
 onClick={() => setPicked(combo)}
 className="text-[11px] font-mono px-2 py-0.5 rounded bg-white border border-keyto hover:bg-slate-50 hover:border-[#2D6BE0]"
 >
 {combo.join(" /")}
 </button>
 )
 )}
 </div>
 </div>
 </div>
 </div>

 {/* Grille de colonnes */}
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
 {accounts.map((a) => (
 <AccountColumn
 key={a.num}
 account={a}
 onRemove={remove}
 onSelect={onSelect}
 />
 ))}
 {accounts.length < 4 && <AccountPicker onPick={add} excludeNums={picked} />}
 </div>
 </div>
 );
}
